// RichTextEditor component matching the HTML design exactly
import React, { useState, useRef } from 'react'

interface RichTextEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  maxLength?: number
  className?: string
  disabled?: boolean
  error?: string
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Describe your product in detail...',
  maxLength = 2000,
  className = '',
  disabled = false,
  error
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const applyFormat = (command: string, value?: string) => {
    if (!textareaRef.current || disabled) return

    const textarea = textareaRef.current
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = textarea.value.substring(start, end)

    let beforeText = textarea.value.substring(0, start)
    let afterText = textarea.value.substring(end)
    let newText = ''

    switch (command) {
      case 'bold':
        newText = `**${selectedText || 'Bold text'}**`
        break
      case 'italic':
        newText = `*${selectedText || 'Italic text'}*`
        break
      case 'list':
        newText = selectedText 
          ? selectedText.split('\n').map(line => `• ${line}`).join('\n')
          : '• List item'
        break
      case 'link':
        newText = `[${selectedText || 'Link text'}](https://example.com)`
        break
      default:
        return
    }

    const fullText = beforeText + newText + afterText
    if (fullText.length <= maxLength) {
      onChange(fullText)
      
      // Set cursor position after the inserted text
      setTimeout(() => {
        const newPosition = start + newText.length
        textarea.setSelectionRange(newPosition, newPosition)
        textarea.focus()
      }, 0)
    }
  }

  return (
    <div className={className}>
      <div
        className={`tw-w-full tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-overflow-hidden focus-within:tw-ring-2 focus-within:tw-ring-primary focus-within:tw-border-transparent tw-transition-all ${
          error ? 'tw-border-red-300 focus-within:tw-border-red-500 focus-within:tw-ring-red-500' : ''
        }`}
      >
        {/* Toolbar */}
        <div className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-300 dark:tw-border-[#364152] tw-bg-gray-50 dark:tw-bg-[#233648] tw-flex tw-gap-2">
          <button
            type="button"
            onClick={() => applyFormat('bold')}
            disabled={disabled}
            className="tw-p-1 tw-rounded hover:tw-bg-gray-200 dark:hover:tw-bg-[#364152] tw-text-gray-500 dark:tw-text-gray-400 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            title="Bold"
          >
            <span className="material-symbols-outlined tw-text-[18px]">format_bold</span>
          </button>
          <button
            type="button"
            onClick={() => applyFormat('italic')}
            disabled={disabled}
            className="tw-p-1 tw-rounded hover:tw-bg-gray-200 dark:hover:tw-bg-[#364152] tw-text-gray-500 dark:tw-text-gray-400 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            title="Italic"
          >
            <span className="material-symbols-outlined tw-text-[18px]">format_italic</span>
          </button>
          <button
            type="button"
            onClick={() => applyFormat('list')}
            disabled={disabled}
            className="tw-p-1 tw-rounded hover:tw-bg-gray-200 dark:hover:tw-bg-[#364152] tw-text-gray-500 dark:tw-text-gray-400 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            title="Bullet List"
          >
            <span className="material-symbols-outlined tw-text-[18px]">format_list_bulleted</span>
          </button>
          <button
            type="button"
            onClick={() => applyFormat('link')}
            disabled={disabled}
            className="tw-p-1 tw-rounded hover:tw-bg-gray-200 dark:hover:tw-bg-[#364152] tw-text-gray-500 dark:tw-text-gray-400 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            title="Link"
          >
            <span className="material-symbols-outlined tw-text-[18px]">link</span>
          </button>
        </div>

        {/* Text Area */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          className="tw-w-full tw-bg-transparent tw-border-none tw-p-4 tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-ring-0 tw-min-h-[140px] tw-resize-none focus:tw-outline-none"
          style={{ outline: 'none' }}
        />
      </div>

      {/* Character Counter */}
      <div className="tw-flex tw-justify-between tw-items-center tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mt-2">
        <span>Max {maxLength} characters.</span>
        <span className={value.length > maxLength * 0.9 ? 'tw-text-orange-600' : ''}>
          {value.length}/{maxLength}
        </span>
      </div>

      {error && (
        <div className="tw-text-sm tw-text-red-600 dark:tw-text-red-400 tw-mt-1">
          {error}
        </div>
      )}
    </div>
  )
}

export default RichTextEditor